






























































































































































































































import dayjs from 'dayjs';
import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import VueI18n from 'vue-i18n';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { RepeatType } from '@/API';
import {
  fetchScheduledHolidayMetadata,
  fetchScheduledHolidayMetadataLabels,
} from '@/appsync/service/EventCalendarQueryService';
import { TextRuleType } from '@/components/atoms/neon/CustomTextField.vue';
import EventCalendarHolidayLabelCard from '@/components/organisms/EventCalendarHolidayLabelCard.vue';
import ScheduleSettingCard from '@/components/organisms/ScheduleSettingCard.vue';
import UpdateHistoryInfo from '@/components/organisms/UpdateHistoryInfo.vue';
import WarningOnAccessFromIosDialog from '@/components/organisms/WarningOnAccessFromIosDialog.vue';
import { refreshStores } from '@/containers/globals/globalSharedService';
import PageNotFound from '@/containers/pages/PageNotFound.vue';
import ErrorMessagePageTemplate from '@/containers/templates/ErrorMessagePageTemplate.vue';
import PortalAppBar from '@/containers/templates/PortalAppBar.vue';
import { i18n } from '@/plugins/i18n/i18n';
import router from '@/router';
import { commonModule } from '@/store/modules/CommonStore';
import { loginUserModule } from '@/store/modules/LoginUserStore';
import {
  UserInfo,
  LoginInfo,
  ScheduledHolidayMetadataWithDetailForFront,
} from '@/types/type';
import {
  getDisabledRoutes,
  isNotEmptyString,
  getSHA3OfTenantLandscape,
  isAccessedFromIosDeviceOrSafariBrowser,
} from '@/utils/CommonUtils';
import {
  callCreateScheduledHolidayMetadata,
  callUpdateScheduledHolidayMetadata,
  endScheduleToday,
  isDateFromAfterToday,
  updateDateFromForCurrentEventMetadata,
} from '@/utils/EventCalendarUtils';
import { newPortalDate, PortalDate } from '@/utils/PortalDate';
import {
  getFormattedDateWithoutTime,
  getRRuleSet,
  getRRuleSetArgs,
} from '@/utils/RRuleUtils';
import {
  AvailableActions,
  UserAction,
  JST_OFFSET_MINS,
  PageType,
  PAGE_PATH_BY_NAME,
  SNACKBAR_TIMEOUT,
  INFINITE_DATE,
} from '@/utils/consts';

Component.registerHooks(['beforeRouteEnter']);
@Component({
  components: {
    PortalAppBar,
    PageNotFound,
    ErrorMessagePageTemplate,
    ScheduleSettingCard,
    EventCalendarHolidayLabelCard,
    UpdateHistoryInfo,
    WarningOnAccessFromIosDialog,
  },
})
export default class EventCalendarHoliday extends Vue {
  @Prop({ required: false, default: false })
  private isReadOnly!: boolean;
  private fromRoute: string = '';

  private loading: boolean = true;
  private isJobSchedulerEnabled: boolean = false;
  private loginInfo!: LoginInfo;
  private hasSchedulerPermission: boolean = false;

  private notEnabledImgAltMessage: String = 'Page Not Enabled';
  private notEnabledImgName: String = 'page-not-enabled';
  private notEnabledImgWidth: String = '250px';
  private notEnabledImgHeight: String = '186px';

  private ruleInputRequired = i18n.tc('portal.ruleInputRequired');

  private isSaved: boolean = false;
  private isAborted: boolean = false;
  private snackbarTimeout: number = SNACKBAR_TIMEOUT;

  private holidayNotFound: boolean = false;
  private pageType: PageType = PageType.create;
  private userAction: UserAction = UserAction.Cancel;

  private showingConfirmationDialog: boolean = false;

  private hasErrorInScheduledSettingCard: boolean = false;
  private hasErrorInEventCalendarHolidayLabelCard: boolean = false;
  private isAccessingFromJST: boolean = false;
  private showAccessFromIosOrSafariWarningDialog: boolean = false;

  private dbHolidayMetadata: ScheduledHolidayMetadataWithDetailForFront | null = null;

  private get moreActions() {
    return [
      {
        text: i18n.tc('common.delete'),
        value: AvailableActions.Abort,
        tooltipText: i18n.tc('jobScheduler.holidays.abortTooltip'),
        enableCondition: this.isEditPage,
      },
    ];
  }

  private get enabledMoreActions() {
    return this.moreActions.filter(action => action.enableCondition);
  }

  private currentHolidayMetadata: ScheduledHolidayMetadataWithDetailForFront = {
    id: uuidv4(),
    title: '',
    description: '',
    repeatType: RepeatType.NoRepeat,
    rruleSet: '',
    labels: [],
    selectedDays: [],
    selectedWeekdays: [],
    dateFrom: null,
    dateTo: null,
    updatedBy: '',
    updatedAt: '',
  };

  private existingLabels: string[] = [];

  public async beforeRouteEnter(to: any, from: any, next: any) {
    next((vm: any) => {
      vm.fromRoute = from.path;
      next();
    });
  }

  public async created(): Promise<void> {
    this.loginInfo = commonModule.loginInfo;
    await refreshStores(this.loginInfo);
    this.hasSchedulerPermission = (this as any).$hasPermission(
      'menus.scheduler'
    );
    const disabledRoutes = await getDisabledRoutes(
      this.loginInfo.tenant,
      this.loginInfo.landscape,
      this.loginInfo.loginName
    );

    if (!disabledRoutes.includes(PAGE_PATH_BY_NAME.eventCalendar)) {
      this.isJobSchedulerEnabled = true;

      const isAccessedFromNotRecommendedEnv: boolean = isAccessedFromIosDeviceOrSafariBrowser();
      if (isAccessedFromNotRecommendedEnv) {
        this.showAccessFromIosOrSafariWarningDialog = true;
      }

      this.isAccessingFromJST =
        new Date().getTimezoneOffset() === JST_OFFSET_MINS;

      this.existingLabels = await fetchScheduledHolidayMetadataLabels(
        this.loginInfo.tenant,
        this.loginInfo.landscape
      );
      const holidayId: string | null = this.$route.params.id as string | null;
      const path: string = this.$route.matched[0].path;
      if (holidayId) {
        const holidayMetadata = await fetchScheduledHolidayMetadata(
          this.loginInfo.tenant,
          this.loginInfo.landscape,
          holidayId
        );
        this.pageCreateOnHolidayIdInUrl(holidayMetadata, path);
      } else {
        this.pageCreateOnNoHolidayIdInUrl(path);
      }
    }
    this.loading = false;
  }

  private get pageTitle() {
    switch (this.pageType) {
      case PageType.edit:
        return this.$t('jobScheduler.holidays.updateHolidayTitle');
      case PageType.copy:
        return this.$t('jobScheduler.holidays.copyHolidayTitle');
      case PageType.view:
        return this.$t('jobScheduler.holidays.viewHolidayTitle');
      case PageType.create:
        return this.$t('jobScheduler.holidays.createHolidayTitle');
      default:
        return this.$t('jobScheduler.holidays.createHolidayTitle');
    }
  }

  private get userInfo(): UserInfo {
    return loginUserModule.userInfo;
  }

  private rules: { [key: string]: TextRuleType } = {
    required: value => !!value || this.ruleInputRequired,
  };

  private pageCreateOnHolidayIdInUrl(
    holidayMetadata: ScheduledHolidayMetadataWithDetailForFront | null,
    path: string
  ) {
    if (holidayMetadata && path === PAGE_PATH_BY_NAME.editHoliday) {
      this.setDataForEditPage(holidayMetadata);
    } else if (holidayMetadata && path === PAGE_PATH_BY_NAME.copyHoliday) {
      this.setDataForCopyPage(holidayMetadata);
    } else if (holidayMetadata && path === PAGE_PATH_BY_NAME.viewHoliday) {
      this.setDataForViewPage(holidayMetadata);
    } else if (path === PAGE_PATH_BY_NAME.createHoliday) {
      this.redirectToCreatePage();
    } else {
      this.holidayNotFound = true;
    }
  }

  private setDataForViewPage(
    holidayMetadata: ScheduledHolidayMetadataWithDetailForFront
  ) {
    this.currentHolidayMetadata = R.clone(holidayMetadata);
    this.dbHolidayMetadata = R.clone(holidayMetadata);
    this.pageType = PageType.view;
  }

  private setDataForEditPage(
    holidayMetadata: ScheduledHolidayMetadataWithDetailForFront
  ) {
    this.currentHolidayMetadata = R.clone(holidayMetadata);
    this.dbHolidayMetadata = R.clone(holidayMetadata);
    this.pageType = PageType.edit;
  }

  private setDataForCopyPage(
    holidayMetadata: ScheduledHolidayMetadataWithDetailForFront
  ) {
    this.currentHolidayMetadata = R.clone(holidayMetadata);
    this.dbHolidayMetadata = R.clone(holidayMetadata);
    this.currentHolidayMetadata.id = uuidv4();
    this.currentHolidayMetadata.title =
      this.$t('jobScheduler.common.copy') + this.currentHolidayMetadata.title;
    this.pageType = PageType.copy;
  }

  private redirectToCreatePage() {
    const movedFromEventCalendarPage = R.startsWith(
      '/eventCalendar',
      this.fromRoute
    );
    if (!movedFromEventCalendarPage) {
      this.currentHolidayMetadata.id = uuidv4();
      router.push({
        name: 'createHoliday',
        params: { id: this.currentHolidayMetadata.id },
      });
    }
    this.pageType = PageType.create;
  }

  private pageCreateOnNoHolidayIdInUrl(path: string) {
    if (path === PAGE_PATH_BY_NAME.createHoliday) {
      this.currentHolidayMetadata.id = uuidv4();
      router.push({
        name: 'createHoliday',
        params: { id: this.currentHolidayMetadata.id },
      });
      this.pageType = PageType.create;
    } else {
      this.holidayNotFound = true;
    }
  }

  private changeHolidayLabels(inputtedHolidayLabels: string[]) {
    this.currentHolidayMetadata.labels = inputtedHolidayLabels;
  }

  private changeDateFrom(dateFrom: string) {
    this.currentHolidayMetadata.dateFrom = dateFrom;
  }

  private changeDateTo(dateTo: string) {
    this.currentHolidayMetadata.dateTo = dateTo;
  }

  private changeRepeatType(selectedRepeatType: RepeatType) {
    this.currentHolidayMetadata.repeatType = selectedRepeatType;
  }

  private changeWeekdays(selectedWeekdays: number[]) {
    this.currentHolidayMetadata.selectedWeekdays = selectedWeekdays;
  }

  private changeDays(selectedDays: number[]) {
    this.currentHolidayMetadata.selectedDays = selectedDays;
  }

  private get isHolidayDetailValid(): boolean {
    return Boolean(
      this.currentHolidayMetadata.title &&
        !this.hasErrorInScheduledSettingCard &&
        !this.hasErrorInEventCalendarHolidayLabelCard
    );
  }

  private validationErrorInScheduledSettingCard(doesErrorExists: boolean) {
    this.hasErrorInScheduledSettingCard = doesErrorExists;
  }
  private validationErrorInEventCalendarHolidayLabelCard(
    doesErrorExists: boolean
  ) {
    this.hasErrorInEventCalendarHolidayLabelCard = doesErrorExists;
  }

  private redirectDate = (dateFrom: string | null) => {
    const [year, month, day] = dateFrom
      ? R.split('-', dateFrom)
      : R.split('-', newPortalDate(dayjs(), 'YYYY-MM-DD').toString());
    return [year, month, day];
  };

  private async doSave() {
    if (this.currentHolidayMetadata.repeatType === RepeatType.NoRepeat) {
      this.currentHolidayMetadata.dateTo = this.currentHolidayMetadata.dateFrom;
    }
    if (!this.currentHolidayMetadata.dateTo) {
      this.currentHolidayMetadata.dateTo = INFINITE_DATE;
    }
    if (this.pageType === PageType.create || this.pageType === PageType.copy) {
      await callCreateScheduledHolidayMetadata(
        this.currentHolidayMetadata,
        this.loginInfo
      );
    } else {
      await this.doUpdateScheduledHolidayMetadata();
    }
    this.isSaved = true;
    const [year, month, day] = this.redirectDate(
      this.currentHolidayMetadata.dateFrom
    );
    router.push({
      name: 'eventCalendar',
      params: {
        period: 'month',
        year: year,
        month: month,
        day: day,
      },
    });
  }

  private async doUpdateScheduledHolidayMetadata() {
    if (isDateFromAfterToday(this.dbHolidayMetadata)) {
      await callUpdateScheduledHolidayMetadata(
        this.currentHolidayMetadata,
        this.loginInfo
      );
    } else {
      await this.endDbHolidayMetadataToday();
      await this.makeCurrentHolidayMetadataStartFromTomorrow();
    }
  }

  private async endDbHolidayMetadataToday() {
    const metadataEndingToday = endScheduleToday(this.dbHolidayMetadata);
    await callUpdateScheduledHolidayMetadata(
      metadataEndingToday,
      this.loginInfo
    );
  }

  private async makeCurrentHolidayMetadataStartFromTomorrow() {
    this.currentHolidayMetadata.id = uuidv4();
    if (
      this.dbHolidayMetadata &&
      this.dbHolidayMetadata.dateFrom === this.currentHolidayMetadata.dateFrom
    ) {
      this.currentHolidayMetadata = updateDateFromForCurrentEventMetadata(
        this.currentHolidayMetadata
      );
    }
    await callCreateScheduledHolidayMetadata(
      this.currentHolidayMetadata,
      this.loginInfo
    );
  }

  private onClickMoreActions(action: AvailableActions) {
    switch (action) {
      case AvailableActions.Abort:
        this.doAbort();
        break;
      default: {
        const inspector: never = action;
      }
    }
  }

  private async doAbort() {
    this.userAction = UserAction.Abort;
    this.showingConfirmationDialog = true;
  }

  private doCancel(): void {
    this.userAction = UserAction.Cancel;
    if (!this.doesAnyChangeExists()) {
      router.push(PAGE_PATH_BY_NAME.eventCalendar);
    } else {
      this.showingConfirmationDialog = true;
    }
  }

  private async onClickAbortInDialog() {
    this.showingConfirmationDialog = false;

    const metadataEndingToday = endScheduleToday(this.currentHolidayMetadata);
    await callUpdateScheduledHolidayMetadata(
      metadataEndingToday,
      this.loginInfo
    );
    this.isAborted = true;
    const [year, month, day] = this.redirectDate(
      this.currentHolidayMetadata.dateFrom
    );
    router.push({
      name: 'eventCalendar',
      params: {
        period: 'month',
        year: year,
        month: month,
        day: day,
      },
    });
  }

  private onClickOkInDialog() {
    this.showingConfirmationDialog = false;
    router.push(PAGE_PATH_BY_NAME.eventCalendar);
  }

  private onClickCancelInDialog() {
    this.showingConfirmationDialog = false;
  }

  private copyCurrentDataToDbData() {
    this.dbHolidayMetadata = R.clone(this.currentHolidayMetadata);
  }

  private doesAnyChangeExists() {
    return !R.equals(this.dbHolidayMetadata, this.currentHolidayMetadata);
  }

  private get isEditPage() {
    return this.pageType === PageType.edit;
  }

  private get isViewPage() {
    return this.pageType === PageType.view;
  }

  private get isAbortAction() {
    return this.userAction === UserAction.Abort;
  }

  private get dialogTitle() {
    return this.userAction === UserAction.Abort
      ? this.$t('jobScheduler.holidays.abortDialogTitle')
      : this.$t('common.discardDialogTitle');
  }

  private get confirmationMessage() {
    return this.userAction === UserAction.Abort
      ? this.$t('jobScheduler.holidays.abortConfirmMessage')
      : this.$t('common.discardConfirmMessage');
  }
}
